// Base Imports
import { useEffect, useState } from 'react'

// Package Imports
import axios from '@services/axiosInterceptorInstance'
import * as Yup from 'yup'
import Cookies from 'js-cookie'

// Component Imports
import ProductWarranty from '../ProductSidebar/ProductWarranty'
import ProductReturn from '../ProductSidebar/ProductReturn'
import DeliveryInfoForm from './DeliveryInfoForm'

// Other Imports
import { matchStrings } from '@framework/utils/parse-util'
import {
  getEligibleReturnReplacementDays,
  getMinMax,
  getReplacementPolicyImages,
  getReturnPolicyImages,
  resetSubmitData,
  submitData,
} from '@framework/utils/app-util'
import {
  DAY_DATE_FORMAT_2,
  DeliveryInfoPageAction,
  Messages,
  NEXT_DELIVERY_INFO,
  TOP_PINCODE,
} from '@components/utils/constants'
import { GA_EVENT } from 'hooks/ga_event.modal'
import { recordGA4Event } from '@components/services/analytics/ga4'
import { formatDate } from '@components/utils/dateFormatter'
import { DAYS_30 } from '@components/utils/textVariables'
import { IMAGE_ASSET_BASE_URL } from '@framework/utils/constants'

export const DELIVERY_FORM_ID = 'deliveryInfoForm'
export const DELIVERY_FORM_FIELDS = [
  {
    type: 'tel',
    name: 'deliveryPinCode',
    placeholder: 'Enter Pincode',
    showLabel: false,
    label: 'Pincode',
    className:
      'px-3 py-4 text-sm font-normal text-black border bg-zinc-100 border-zinc-100 placeholder:text-gray-400 hover:border-gray-900',
    labelClassName: 'text-gray-700 text-sm dark:text-black',
    required: true,
    disabled: false,
    max: 6,
    min: 6,
    handleChange: (e: any, item: any, context: any) => {
      const regex = /^[0-9\s]*$/
      if (regex.test(e?.target.value.toString())) {
        context.setFieldValue(DELIVERY_FORM_FIELDS[0]?.name, e.target.value)
      }
    },
  },
]
export const DELIVERY_FORM_SCHEMA = Yup.object().shape({
  deliveryPinCode: Yup.string()
    .required(Messages.Validations.DeliveryInfo['VALID_PIN'])
    .min(6, Messages.Validations.DeliveryInfo['VALID_PIN'])
    .max(6, Messages.Validations.DeliveryInfo['PIN_CODE_MAX_LENGTH'])
    .matches(
      Messages.Validations.RegularExpressions.MOBILE_NUMBER,
      Messages.Validations.DeliveryInfo['PIN_CODE_INPUT']
    ),
  otp: Yup.number().min(6),
})
export const DEFAULT_DELIVERY_FORM_VALUES = {
  deliveryPinCode: '',
}

interface IDevileryInfo {
  product: any
  grpData: any
  submitState: any
  submitDispatch: any
  stockCode: any
  currentItemStock: number
}

export default function DeliveryInfo(props: IDevileryInfo) {
  const {
    product,
    grpData,
    submitState,
    submitDispatch,
    stockCode,
    currentItemStock,
  } = props

  const [isWarranty, setWarranty] = useState(false)
  const [isReturn, setReturn] = useState(false)
  const [edd, setEDD] = useState<string | undefined>(undefined)
  const [deliveryPinCode, setDeliveryPinCode] = useState('')
  const pincode = Cookies.get('pincode')

  const onSubmit = async (data: any) => {
    setEDD(undefined)
    submitData(submitDispatch, DeliveryInfoPageAction.COMPUTE_EDD)

    const { data: deliveryInfoResult }: any = await axios.post(
      NEXT_DELIVERY_INFO,
      {
        stockCode: product?.stockCode,
        postCode: data?.deliveryPinCode,
      }
    )

    if (deliveryInfoResult?.availabilities?.length) {
      Cookies.set('pincode', data?.deliveryPinCode)
      const minMaxSlaDays = getMinMax(
        deliveryInfoResult?.availabilities,
        'slaDays'
      )
      if (minMaxSlaDays.min != Number.POSITIVE_INFINITY) {
        const newDate = new Date()
        newDate.setDate(
          newDate.getDate() +
            (data.slaDays || deliveryInfoResult?.availabilities?.[0]?.slaDays)
        )
        const formattedDate = formatDate(newDate, DAY_DATE_FORMAT_2)

        setEDD(formattedDate)
        recordGA4Event(window, GA_EVENT.CHECK_PINCODE, {
          current_page: 'PDP',
          pincode_value: data?.deliveryPinCode,
          delivery_expected: minMaxSlaDays.min,
        })
      }
    } else {
      setEDD('')
      recordGA4Event(window, GA_EVENT.CHECK_PINCODE, {
        current_page: 'PDP',
        pincode_value: data?.deliveryPinCode,
        delivery_expected: '',
      })
    }
    resetSubmitData(submitDispatch)
  }

  const [replaceValue, replaceInfo] = useState(grpData)

  const handleClick = (grpData: any) => () => {
    replaceInfo(grpData)
    setWarranty(true)
    recordGA4Event(window, GA_EVENT.POLICY_POPUP, {
      current_page: 'PDP',
      category: 'Warranty',
    })
  }

  const handleReturn = () => {
    setReturn(true)
    recordGA4Event(window, GA_EVENT.POLICY_POPUP, {
      current_page: 'PDP',
      category: 'Easy Returns & Exchange',
    })
  }

  const setInitialData = async (stockCodeParam: any) => {
    const { data: deliveryInfo }: any = await axios.post(NEXT_DELIVERY_INFO, {
      stockCode: stockCodeParam,
      postCode: pincode || TOP_PINCODE,
    })
    const [data] = deliveryInfo.availabilities || []
    if (data?.slaDays) {
      if (pincode) setDeliveryPinCode(pincode)
      const newDate = new Date()
      newDate.setDate(newDate.getDate() + data.slaDays)
      const formattedDate = formatDate(newDate, DAY_DATE_FORMAT_2)
      setEDD(formattedDate)
    }
  }

  useEffect(() => {
    setInitialData(stockCode)
  }, [stockCode])

  // moved inside component to get access to state setter function
  const DELIVERY_FORM_FIELDS2 = [
    {
      type: 'tel',
      name: 'deliveryPinCode',
      placeholder: 'Enter Pincode',
      showLabel: false,
      label: 'Pincode',
      className:
        'px-3 py-4 text-sm font-normal text-black border bg-zinc-100 border-zinc-100 placeholder:text-gray-400 hover:border-gray-900',
      labelClassName: 'text-gray-700 text-sm dark:text-black',
      required: true,
      disabled: false,
      max: 6,
      min: 6,
      handleChange: (e: any, item: any, context: any) => {
        setEDD(undefined)
        context.setErrors({})
        const regex = /^[0-9\s]*$/
        if (regex.test(e?.target.value.toString())) {
          context.setFieldValue(DELIVERY_FORM_FIELDS[0]?.name, e.target.value)
        }
      },
    },
  ]

  let returnEligeble = ''
  let replacementEligeble = ''

  if (grpData['product.replacementeligibility']?.length > 0) {
    const data = grpData['product.replacement'].find((x: any) => x.value)
    replacementEligeble = data?.value?.toLowerCase()
  }

  if (grpData['product.returnandexchangeeligibility']?.length > 0) {
    const data = grpData['product.returnsandexchange'].find((x: any) => x.value)
    returnEligeble = data?.value?.toLowerCase()
  }

  return (
    <div className="flex-1 order-6 w-full sm:order-5">
      <div className="w-full">
        <div className="flex flex-col sm:hidden">
          <div className="section-devider" />
        </div>

        <section
          aria-labelledby="details-heading"
          className="mt-0 border-gray-200 sm:border-t sm:mt-2"
        >
          <div className="flex flex-col px-4 pt-0 pb-0 mt-0 sm:pb-6 sm:pt-2 sm:mt-2 sm:px-0">
            <div className="mb-2 font-bold text-16 text-primary ">
              Free Delivery{' '}
              {edd ? (
                <span className="font-normal text-brown-light">
                  {' '}
                  above ₹600, by <span className="text-green">{edd}</span>
                </span>
              ) : (
                <span className="font-normal text-brown-light">
                  {' '}
                  above ₹600 <span className="text-green ">in 2-3 days</span>
                </span>
              )}
            </div>
            {!pincode && (
              <p className="font-normal text-16 text-brown-light">
                Enter your pincode to check exact date
              </p>
            )}
            <DeliveryInfoForm
              key={deliveryPinCode}
              submitState={submitState}
              formId={DELIVERY_FORM_ID}
              formFields={DELIVERY_FORM_FIELDS2}
              schema={DELIVERY_FORM_SCHEMA}
              onSubmit={onSubmit}
              defaultValues={{ deliveryPinCode }}
              setEDD={setEDD}
              edd={edd}
            />

            {edd && pincode && !matchStrings(edd, '', true) && (
              <div className="w-full msg-info">
                <p className="text-base text-primary">
                  <span className="mr-2 leading-none align-middle">
                    <i className="sprite-icon sprite-delivery-info-icon"></i>
                  </span>
                  Get it by {edd}
                </p>
                <p className="mt-2 text-base text-primary">
                  <span className="mr-2 leading-none align-middle">
                    <i className="sprite-icon pay-delivery-info-icon"></i>
                  </span>
                  Pay on Delivery Available
                </p>
              </div>
            )}

            {currentItemStock != 0 && edd != undefined && edd == '' && (
              <div className="w-full msg-info">
                <p className="text-base text-red-500 flex items-center">
                  <span className="relative mr-1 ">
                    <img
                      alt="EDD"
                      src={`${IMAGE_ASSET_BASE_URL}/icons/not-shipped-edd.svg`}
                      width={20}
                      height={20}
                      className="relative inline-block mr-1 "
                    />
                  </span>
                  <span> Sorry, We don't ship to this pincode.</span>
                </p>
              </div>
            )}

            {currentItemStock === 0 && edd != undefined && edd == '' && (
              <div className="w-full msg-info">
                <p className="text-base text-red-500 flex items-center">
                  <span className="relative mr-1 ">
                    <img
                      alt="EDD"
                      src={`${IMAGE_ASSET_BASE_URL}/icons/not-shipped-edd.svg`}
                      width={20}
                      height={20}
                      className="relative inline-block mr-1 "
                    />
                  </span>
                  <span> The selected size is out of stock :(</span>
                </p>
              </div>
            )}

            <div className="flex flex-col mt-6 gap-4">
              {grpData['product.replacementeligibility']?.length &&
                replacementEligeble == 'true' && (
                  <div className="flex items-center content-center align-center">
                    {grpData['product.replacementeligibility']?.map(
                      (replaceAttr: any, rpdx: number) => {
                        let replaceEligibleDays: number | null =
                          getEligibleReturnReplacementDays(replaceAttr)
                        return (
                          <div key={`product-${rpdx}-delivery`} className="flex items-center w-full">
                            <div className="inline-block cursor-pointer" onClick={handleClick(grpData)}>
                              <img
                                src={`${IMAGE_ASSET_BASE_URL}/icons/${getReplacementPolicyImages(
                                  replaceAttr
                                )}.svg`}
                                alt={replaceAttr?.value}
                                style={{
                                  height: '100%',
                                  width: '60px',
                                }}
                              />
                            </div>
                            <div className="pl-4 font-medium text-primary flex flex-col gap-1 items-start">
                              <div className='text-[1rem] leading-4'>
                                {replaceAttr?.value?.includes(DAYS_30)
                                  ? `${replaceEligibleDays} Day 1st Try Guarantee`
                                  : `${replaceEligibleDays} Day Replacement Warranty`}
                              </div>
                              <div className='text-[0.65rem] leading-3 text-brown-light'>
                                First buy? {replaceEligibleDays} days to try it, easy returns. <span className="text-orange-600 cursor-pointer" onClick={handleClick(grpData)}>More info+</span>
                              </div>
                            </div>
                          </div>
                        )
                      }
                    )}
                  </div>
                )}
              {grpData['product.returnandexchangeeligibility']?.length &&
                returnEligeble == 'true' && (
                  <div className="flex items-center content-center align-center">
                    {grpData['product.returnandexchangeeligibility']?.map(
                      (returnAttr: any, rdx: number) => {
                        let returnEligibleDays: any =
                          getEligibleReturnReplacementDays(returnAttr)

                        return (
                          <div key={`product-${rdx}-edd-return`} className="flex items-center w-full">
                            <div className="inline-block cursor-pointer" onClick={() => handleReturn()}>
                              <img
                                src={`${IMAGE_ASSET_BASE_URL}/icons/${getReturnPolicyImages(
                                  returnAttr
                                )}.svg`}
                                alt={returnAttr?.value}
                                style={{
                                  height: '100%',
                                  width: '60px',
                                }}
                              />
                            </div>
                            <div className="pl-4 font-medium text-primary flex flex-col gap-1 items-start">
                              <div className='text-[1rem] leading-4'>
                                {returnEligibleDays} Day Easy Returns & Exchange
                              </div>
                              <div className='text-[0.65rem] leading-3 text-brown-light'>
                                Unsatisfied? Return in {returnEligibleDays} days, no fuss. <span className="text-orange-600 cursor-pointer" onClick={() => handleReturn()}>More info+</span>
                              </div>
                            </div>
                          </div>
                        )
                      }
                    )}
                  </div>
                )}
            </div>
          </div>
        </section>
        <ProductWarranty
          isWarranty={isWarranty}
          setWarranty={setWarranty}
          replaceValue={replaceValue}
        />
        <ProductReturn
          isReturn={isReturn}
          setReturn={setReturn}
          data={grpData['product.returnandexchangeeligibility']}
        />
      </div>
    </div>
  )
}
